import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { isTokenExpired } from '@/lib/auth';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2 } from 'lucide-react';
import { useAuthStore, useAuthActions, useIsImpersonating } from '@/stores/auth.store';
import { useTenantStore } from '@/stores/tenant.store';
import { useLogin, clearSession } from '@/hooks/useAuth';
import { useStopImpersonation } from '@/hooks/useImpersonation';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/atoms/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/atoms/form';
import { PublicCenteredPage } from '@/components/public/PublicCenteredPage';

const LAST_EMAIL_KEY = 'notifi:last-email';

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});

type LoginFormValues = z.infer<typeof loginSchema>;

// Require an absolute path but reject protocol-relative URLs like //evil.com,
// which browsers resolve as https://evil.com — an open redirect vector.
const searchSchema = z.object({
  redirect: z
    .string()
    .regex(/^\/(?!\/)/)
    .optional(),
  // Marker the password-reset / create-password routes pass on navigate so the
  // login route can defensively wipe any lingering session before the next
  // user types their credentials. The useResetPassword hook already calls
  // clearSession() in its onSuccess, but this is a no-cost safety net for
  // edge cases (e.g. the user opens /login in another tab after the reset).
  from: z.literal('reset').optional(),
});

export const Route = createFileRoute('/(public)/login')({
  validateSearch: searchSchema,
  beforeLoad: ({ search }) => {
    // Defensive scrub: if the user arrived here from a password reset, drop
    // any lingering session BEFORE the auto-redirect check below — otherwise
    // a stale-but-unexpired token would bounce them straight into the prior
    // user's dashboard.
    if (search.from === 'reset') {
      clearSession();
      return;
    }
    const { token, impersonation } = useAuthStore.getState();
    // During impersonation we intentionally keep the login page reachable so
    // the operator can stop impersonating and start a fresh session — but we
    // skip the normal "already signed in" auto-redirect because the
    // impersonated session is the wrong session for that.
    if (token && !isTokenExpired(token) && !impersonation.active) {
      // Default to /dashboard (not /dashboard/home) so the onboarding gate can
      // send unfinished tenants to the setup checklist. An explicit deep-link
      // redirect is still honored.
      throw redirect({ to: search.redirect ?? '/dashboard', replace: true });
    }
  },
  component: LoginPage,
});

function LoginPage() {
  const { redirect: redirectTo } = Route.useSearch();
  const navigate = useNavigate();
  const { login } = useAuthActions();
  const loginMutation = useLogin();
  const isImpersonating = useIsImpersonating();
  const stopImpersonation = useStopImpersonation({ fallbackUrl: '/login' });

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: localStorage.getItem(LAST_EMAIL_KEY) ?? '',
      password: '',
    },
  });

  async function onSubmit(values: LoginFormValues) {
    form.clearErrors('root');
    // Guard: refuse to authenticate while impersonating. Otherwise the
    // operator could accidentally start a fresh session on top of the
    // impersonated one and mix audit trails. They have to explicitly stop
    // first.
    if (isImpersonating) {
      form.setError('root', {
        message: 'End the current impersonation session before logging in as another user.',
      });
      return;
    }
    try {
      const data = await loginMutation.mutateAsync(values);
      localStorage.setItem(LAST_EMAIL_KEY, values.email);
      login({
        userId: data.userId,
        email: values.email,
        token: data.token,
        refreshToken: data.refreshToken,
        tenantId: data.tenantId,
        roles: data.roles,
        permissions: data.permissions,
        permissionsVersion: data.permissionsVersion,
        permissionScopes: data.permissionScopes,
        capabilityFlags: data.capabilityFlags,
        fieldMasks: data.fieldMasks,
        technicianId: data.technicianId,
      });
      useTenantStore.getState().setTenant({
        tenantId: data.tenantId,
        tenantName: '',
        plan: 'pro',
      });
      // Route through /dashboard so the onboarding gate runs (see login
      // beforeLoad). Explicit deep-link redirects are preserved.
      navigate({ to: redirectTo ?? '/dashboard', replace: true });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred';
      form.setError('root', { message });
    }
  }

  return (
    <PublicCenteredPage>
      <Card className="w-full max-w-sm">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold">Sign in</CardTitle>
          <CardDescription>Enter your email and password to continue</CardDescription>
        </CardHeader>
        <CardContent>
          {isImpersonating && (
            <div
              role="alert"
              className="mb-4 rounded-md border border-red-500/40 bg-red-50 px-3 py-2 text-sm text-red-900 dark:bg-red-950/40 dark:text-red-100"
            >
              <p className="font-semibold">An impersonation session is active.</p>
              <p className="mt-1">
                End it before signing in as another user. Otherwise the audit trail would mix two
                identities.
              </p>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                className="mt-2"
                onClick={() => void stopImpersonation()}
              >
                Stop impersonating
              </Button>
            </div>
          )}
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="you@example.com"
                        autoComplete="email"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center justify-between">
                      <FormLabel>Password</FormLabel>
                      <a
                        href="/forgot-password"
                        className="text-sm text-muted-foreground underline-offset-4 hover:underline"
                        onClick={(e) => {
                          e.preventDefault();
                          navigate({ to: '/forgot-password' });
                        }}
                      >
                        Forgot password?
                      </a>
                    </div>
                    <FormControl>
                      <Input
                        type="password"
                        placeholder="••••••••"
                        autoComplete="current-password"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {form.formState.errors.root && (
                <p role="alert" className="text-sm text-destructive">
                  {form.formState.errors.root.message}
                </p>
              )}

              <Button
                type="submit"
                className="w-full"
                disabled={loginMutation.isPending || isImpersonating}
              >
                {loginMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                    Signing in…
                  </>
                ) : (
                  'Sign in'
                )}
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>
    </PublicCenteredPage>
  );
}
